import { IFieldModel } from '@checkout/fields';
import { IAddress, IAddressFields, ICountry, ILocation, RootState } from '@checkout/state';
import * as cx from 'classnames';
import { Field } from 'formik';
import { Fragment } from 'react';
import * as React from 'react';
import { ChangeEvent } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { countryChangeRequest, countryStateChangeRequest } from '../../../reducers/checkout';
import { getOptionsData } from '../../../utils/address';
import { FormNames } from '../../../utils/formNames';
import { GroupTitle, InputBoxField } from '../../presentational';
import messages from './../../../i18n/base-en';
import * as Styles from './AddressForm.scss';
import { getAddressFormModel } from './AddressFormModel';

interface IOwnProps {
  switchable?: boolean;
  location: ILocation;
  isActive: boolean;
  fields: IAddressFields;
  formName: string;
  readOnly?: boolean;
  disableCountryStateFields?: boolean;
}

interface IConnectedFormProps extends IAddress {}

interface IConnectedProps {
  countries: ICountry[];
  billingCountryCode: string | null;
  shippingCountryCode: string | null;
}

interface IConnectetDispatchProps {
  countryChangeRequest: typeof countryChangeRequest;
  countryStateChangeRequest: typeof countryStateChangeRequest;
}

type Props = IOwnProps & IConnectetDispatchProps & IConnectedProps & IConnectedFormProps & InjectedIntlProps;

interface IOwnState {
  isActive: boolean;
}

class AddressForm extends React.Component<Props, IOwnState> {

  state: IOwnState = {
    isActive: this.props.isActive,
  };

  toggleActive = () => {
    this.setState({
      isActive: !this.state.isActive,
    });
  }

  handleCountryChange = (event: ChangeEvent<any>) => {
    const { formName } = this.props;
    const countryCode = event.target.value;

    this.props.countryChangeRequest({ countryCode, formName });
  }

  handleCountryStateChange = (event: ChangeEvent<any>) => {
    const { formName, billingCountryCode, shippingCountryCode } = this.props;
    const countryCode = formName === FormNames.billingAddress ? billingCountryCode : shippingCountryCode;
    const stateCode = event.target.value;

    this.props.countryStateChangeRequest({ countryCode, stateCode, formName });
  }

  handleDropdownChange = (event: ChangeEvent<any>, dropdownFor: 'Country' | 'State' | undefined) => {
    if (dropdownFor === 'Country') {
      this.handleCountryChange(event);
    } else if (dropdownFor === 'State') {
      this.handleCountryStateChange(event);
    }
  }

  handleZipCode = (event: ChangeEvent<any>, country: string | null) => {
    if (country === 'US') {
      if (event.target.value.length > 5) {
        const value = event.target.value.split('-').join('');
        event.target.value = value.substring(0, 5) + '-' + value.substring(5);
      }
    }
  }

  render() {
    const { isActive } = this.state;
    const {
      intl,
      switchable,
      fields,
      readOnly,
      formName,
      countries,
      billingCountryCode,
      shippingCountryCode,
      disableCountryStateFields,
    } = this.props;
    const { formatMessage } = intl;
    const showAddressFields = !switchable || (switchable && isActive);
    const country = formName === FormNames.billingAddress ? billingCountryCode : shippingCountryCode;

    const adderessFormModel =
      fields && getAddressFormModel(fields, formName, intl, !switchable, disableCountryStateFields, country);
    if (!fields || !adderessFormModel) { return null; }

    const addressClassNames = cx({
      [Styles.form]: true,
      [Styles.addressReadOnly]: readOnly,
    });

    return (
      <div className={addressClassNames}>
        <Fragment>
          {switchable && (
            <div className={Styles.checkbox}>
              <GroupTitle title={formatMessage(messages.billing_address_group_title)} />
              <Field
                id={`${FormNames.billingAddress}.billingSameAsShipping`}
                name={`${FormNames.billingAddress}.billingSameAsShipping`}
                component={InputBoxField}
                semanticType="checkbox"
                onChange={this.toggleActive}
                variant="toggle"
                label={formatMessage(messages.billing_address_checkbox_label)}
              />
            </div>
          )}
          {showAddressFields && (
            <div className={Styles.fields}>
              {adderessFormModel.map((field: IFieldModel, index: number) => {
                const { type, visible, name, validate, dropdownFor, ...fieldProps } = field;
                const options = getOptionsData(field.dropdownFor, countries, country);

                // Do not render State field for countries without state options because we can not rely on API response
                if (dropdownFor === 'State' && !options) { return null; }

                return visible && (
                  <Field
                    {...fieldProps}
                    key={`${formName}.${name}`}
                    id={`${formName}.${name}`}
                    name={`${formName}.${name}`}
                    options={options}
                    readOnly={readOnly}
                    validate={validate}
                    onChange={(event: ChangeEvent<any>) => {
                      if (type === 'select') {
                        this.handleDropdownChange(event, dropdownFor);
                      }

                      if (name === 'zipCode') {
                        this.handleZipCode(event, country);
                      }
                    }}
                  />
                );
              })}
            </div>
          )}
        </Fragment>
      </div>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      countries: state.checkout.countries,
      billingCountryCode: state.checkout.fieldsData.billingAddress && state.checkout.fieldsData.billingAddress.country,
      shippingCountryCode: state.checkout.fieldsData.shippingAddress && state.checkout.fieldsData.shippingAddress.country,
    }),
    {
      countryChangeRequest,
      countryStateChangeRequest
    },
  )
)(AddressForm);
